const {
  groupBy,
  sumBy,
  isEmpty,
  get,
  minBy,
  round,
  sum,
  keyBy,
  startCase,
} = require('lodash')

const {
  getProficiencyBand,
  formatDate,
  TEST_UNIQ_ID_SEPARATOR,
  performanceBandKeys,
  percentage,
  getShortTestName,
} = require('../../common')
const {
  ANALYZE_BY_AVERAGE_SCORE_TABLE_PAGE_SIZE,
  ANALYZE_BY_PERFORMANCE_DISTRIBUTION_TABLE_PAGE_SIZE,
  ANALYZE_BY_KEYS,
  ANALYZE_BY_OPTIONS,
  compareByKeys,
  compareByMap,
  compareByOptionsInfo,
  EXTERNAL_SCORE_TYPES,
  EXTERNAL_SCORE_PREFIX,
  EXTERNAL_SCORE_SUFFIX,
  sortKeys,
  tableColumnsData,
} = require('../common/constants')
const {
  getAchievementLevels,
  getScoreLabel,
} = require('../../../const/dataWarehouse')
const { TEST_TYPE_LABELS } = require('../../../const/testTypes')

const getAllTestTypeLabels = (userTestTypesInternal = {}) => {
  return Object.values(userTestTypesInternal).reduce(
    (pv, { title }) => ({ ...pv, [title]: title }),
    TEST_TYPE_LABELS
  )
}

const getExternalScoreFormattedByType = ({
  externalScore,
  externalScoreType,
  formatScore = true,
}) => {
  if (externalScore == null || Number.isNaN(externalScore)) return NaN
  let score = Math.abs(round(externalScore, 0))
  if (formatScore) {
    score = new Intl.NumberFormat().format(score)
  }
  const externalScorePrefix =
    externalScore < 0 ? EXTERNAL_SCORE_PREFIX[externalScoreType] : ''
  const externalScoreSuffix = EXTERNAL_SCORE_SUFFIX[externalScoreType] || ''
  return `${externalScorePrefix || ''}${score}${externalScoreSuffix}`
}

const getSelectedExternalScore = ({
  score,
  lexileScore,
  quantileScore,
  percentile,
  externalScoreType,
}) => {
  let externalScore = score
  if (
    externalScoreType == EXTERNAL_SCORE_TYPES.LEXILE_SCORE &&
    !Number.isNaN(lexileScore)
  ) {
    externalScore = lexileScore
  } else if (
    externalScoreType == EXTERNAL_SCORE_TYPES.QUANTILE_SCORE &&
    !Number.isNaN(quantileScore)
  ) {
    externalScore = quantileScore
  } else if (
    externalScoreType == EXTERNAL_SCORE_TYPES.PERCENTILE &&
    !Number.isNaN(percentile)
  ) {
    return percentile
  }
  return externalScore
}

const getTestUniqId = (test) =>
  [test.testId || test.testName, test.termId].join(TEST_UNIQ_ID_SEPARATOR)

const groupByCompareByKey = (metricInfo, compareBy) => {
  switch (compareBy) {
    case 'district':
      return groupBy(metricInfo, 'districtId')
    case 'school':
      return groupBy(metricInfo, 'schoolId')
    case 'student':
      return groupBy(metricInfo, 'studentId')
    case 'class':
    case 'group':
      return groupBy(metricInfo, 'groupId')
    case 'teacher':
      return groupBy(metricInfo, 'teacherId')
    case 'race':
      return groupBy(metricInfo, 'race')
    case 'gender':
      return groupBy(metricInfo, 'gender')
    case 'ellStatus':
      return groupBy(metricInfo, 'ellStatus')
    case 'iepStatus':
      return groupBy(metricInfo, 'iepStatus')
    case 'hispanicEthnicity':
      return groupBy(metricInfo, 'hispanicEthnicity')
    case 'frlStatus':
      return groupBy(metricInfo, 'frlStatus')
    case 'standard':
      return groupBy(metricInfo, 'standardId')
    default:
      return {}
  }
}

const findTestWithAverageBand = (tests) => {
  const NO_RANK = 0
  const weightedAverageRank =
    sum(
      tests.map((t) => get(t, 'band.rank', NO_RANK) * t.totalStudentCount || 0)
    ) / sumBy(tests, 'totalStudentCount')
  const item = minBy(
    tests,
    (el) => get(el, 'band.rank', NO_RANK) - weightedAverageRank
  )
  return item || {}
}

const getWeightedAchievementLevel = (records) => {
  const weightedAverage =
    sum(
      records.map(
        (record) => (record.achievementLevel || 0) * (record.totalGraded || 0)
      )
    ) / sum(records.map((record) => parseInt(record.totalGraded || 0, 10)))

  return round(weightedAverage || 0, 2)
}

const augmentBandData = (tests, bandInfo, externalBands) => {
  const testsWithBandInfo = tests.map(({ ...t }) => {
    let band = { name: '-', color: '#010101' }
    if (t.externalTestType) {
      const achievementLevels = getAchievementLevels(
        { ...t, title: t.testTitle },
        externalBands
      )
      Object.assign(t, { bands: achievementLevels })
      band = achievementLevels.find((al) => al.active) || band
    } else {
      band = getProficiencyBand(t.score, bandInfo) || band
    }
    Object.assign(t, { band, distribution: t.distribution || [] })
    return t
  })
  return testsWithBandInfo
}

const getExternalScores = (
  lexileScore,
  quantileScore,
  percentile,
  totalStudentCount
) =>
  [
    [lexileScore, EXTERNAL_SCORE_TYPES.LEXILE_SCORE],
    [quantileScore, EXTERNAL_SCORE_TYPES.QUANTILE_SCORE],
    [percentile, EXTERNAL_SCORE_TYPES.PERCENTILE],
  ].map(([score, scoreType]) => {
    const _score = score / totalStudentCount
    return getExternalScoreFormattedByType({
      externalScore: _score,
      externalScoreType: scoreType,
      formatScore: false,
    })
  })

const getExternalScoreLabels = (record, externalTestType) =>
  Object.values(EXTERNAL_SCORE_TYPES).reduce((acc, externalScoreType) => {
    const score = Math.round(record[externalScoreType])
    acc[externalScoreType] =
      externalScoreType === EXTERNAL_SCORE_TYPES.SCALED_SCORE
        ? getScoreLabel(score, { externalTestType })
        : getExternalScoreFormattedByType({
            externalScore: score,
            externalScoreType,
            formatScore: false,
          })
    return acc
  }, {})

// chart data transformers
const processExternalTestRow = (
  test,
  externalBands,
  externalBandsMapped,
  termsKeyedById
) => {
  const totalGraded = test.totalStudentCount
  const selectedTerm = termsKeyedById[test.termId]
  const testData = {
    ...test,
    testId: test.testName,
    testTitle: test.testTitle,
    title: test.testTitle,
    testType: test.externalTestType,
    externalTestType: test.externalTestType,
    externalTestTypeLabel:
      externalBandsMapped[test.externalTestType]?.testCategoryLabel ??
      test.externalTestType,
    shortTestName: getShortTestName(test.externalTestType, test.testName),
    assessmentDate: +new Date(test.assessmentDate),
    totalGraded,
    termName: selectedTerm?.name,
    termEndDate: selectedTerm?.endDate,
    testUniqId: getTestUniqId(test),
  }

  const _achievementLevels = getAchievementLevels(testData, externalBands)
  testData.bands = _achievementLevels
  testData.band = _achievementLevels.find((al) => al.active)
  const distributionByLevel = keyBy(test.distribution || [], (d) =>
    String(d.bandScore)
  )

  const records = _achievementLevels.map((band) => {
    const dist = distributionByLevel[String(band.id)] || {}
    const bandTotalGraded = +(dist.studentCount || 0)
    const studentPerc = totalGraded
      ? round(percentage(bandTotalGraded, totalGraded), 2)
      : 0
    return {
      totalGraded: bandTotalGraded,
      studentPerc,
      id: band.id,
      color: band.color,
      bandName: band.name,
      insideLabels: {
        studentPerc,
        ...getExternalScoreLabels(dist, testData.externalTestType),
      },
    }
  })

  return {
    ...testData,
    records,
    topLabels: getExternalScoreLabels(test, testData.externalTestType),
  }
}

const processInternalTestRow = (
  test,
  bandInfo,
  userTestTypesInternal,
  termsKeyedById
) => {
  const { totalStudentCount: totalGraded, score, distribution, termId } = test
  const distributionByBand = keyBy(distribution || [], (d) =>
    String(d.bandScore)
  )
  const selectedTerm = termsKeyedById[termId]

  const records = bandInfo.map((band) => {
    const dist = distributionByBand[String(band.threshold)] || {}
    const { studentCount, bandScore, score: distScore } = dist
    const { name, color, threshold, aboveStandard } = band
    const studentPerc = totalGraded
      ? round(percentage(studentCount, totalGraded), 2)
      : 0
    return {
      totalGraded: studentCount,
      studentPerc,
      bandScore,
      threshold,
      color,
      bandName: name,
      aboveStandard,
      insideLabels: {
        studentPerc,
        [EXTERNAL_SCORE_TYPES.SCALED_SCORE]: `${Math.round(distScore)}%`,
      },
    }
  })

  return {
    testId: test.testId,
    testType: test.testType,
    termId: test.termId,
    assessmentDate: parseInt(test.assessmentDate, 10),
    termName: selectedTerm?.name,
    termEndDate: selectedTerm?.endDate,
    testUniqId: getTestUniqId(test),
    testName: `${test.testName} (${
      getAllTestTypeLabels(userTestTypesInternal)[test.testType]
    })`,
    isIncomplete: false,
    totalGraded,
    maxPossibleScore: test.maxPossibleScore,
    records,
    topLabels: {
      [EXTERNAL_SCORE_TYPES.SCALED_SCORE]: `${Math.round(score)}%`,
    },
  }
}

/**
 * Builds chart render data from edu-api combined `chartData` (pre-aggregated per test).
 * Internal vs external is determined by presence of `externalTestType`.
 */
const getChartData = (
  chartData = [],
  selectedPerformanceBand = [],
  externalBands = [],
  termsKeyedById = {},
  userTestTypesInternal
) => {
  if (isEmpty(chartData)) {
    return []
  }

  const externalBandsMapped = keyBy(externalBands, 'testCategory')

  const curatedChartData = chartData
    .map((test) => {
      const isExternal = !!test.externalTestType
      if (isExternal) {
        return processExternalTestRow(
          test,
          externalBands,
          externalBandsMapped,
          termsKeyedById
        )
      }
      if (!isEmpty(selectedPerformanceBand)) {
        return processInternalTestRow(
          test,
          selectedPerformanceBand,
          userTestTypesInternal,
          termsKeyedById
        )
      }
      return null
    })
    .filter(Boolean)

  return curatedChartData.sort(
    (a, b) =>
      a.assessmentDate - b.assessmentDate ||
      a.testName.toLowerCase().localeCompare(b.testName.toLowerCase())
  )
}

// table data transformers

const getTestColumnsData = (metricInfo, userTestTypesInternal) => {
  const result = {}
  metricInfo.forEach((currentMetric) => {
    const { testName = 'N/A', testUniqId, ...testData } = currentMetric
    const isExternal = !!currentMetric.externalTestType

    const scoreLabels = getExternalScoreLabels(
      testData,
      testData.externalTestType
    )

    const _testName = isExternal
      ? testName
      : `${testName} (${
          getAllTestTypeLabels(userTestTypesInternal)[testData.testType]
        })`

    result[testUniqId] = {
      ...testData,
      ...scoreLabels,
      testUniqId,
      testName: _testName,
    }
  })
  return result
}

const getTableData = (
  orgData,
  reportTableData,
  filtersData,
  feedTypes,
  bandInfo = [],
  compareByKey,
  isDistrictGroupAdmin,
  userTestTypesInternal
) => {
  const { metricInfo = [] } = get(reportTableData, 'data.result', {})
  const { externalBands = [] } = get(filtersData, 'data.result', {})
  const feedTypesMap = keyBy(feedTypes, 'key')
  const { districts: districtsInfo = [] } = orgData?.districtGroup || {}

  const filteredMetricInfo = metricInfo
    .map(({ ...item }) => {
      const testUniqId = getTestUniqId(item)
      const isExternal = !!feedTypesMap[item.testType]

      if (isExternal) {
        const districtName =
          isDistrictGroupAdmin && !item.districtName
            ? districtsInfo.find((d) => d._id === item.districtId)?.name
            : item.districtName
        Object.assign(item, {
          externalTestType: item.testType,
          externalTestTypeLabel: feedTypesMap[item.testType].title,
          districtName,
          assessmentDate: +new Date(item.assessmentDate),
        })
      }
      return { ...item, testUniqId, title: item.testTitle }
    })
    .filter(
      ({ externalTestType }) =>
        (!isEmpty(bandInfo) && !externalTestType) ||
        (!isEmpty(externalBands) && externalTestType)
    )

  const metricInfoWithBandData = augmentBandData(
    filteredMetricInfo,
    bandInfo,
    externalBands
  )

  const groupedByCompareByKey = groupByCompareByKey(
    metricInfoWithBandData,
    compareByKey
  )
  const compareByLabelKey = compareByMap[compareByKey]
  const isStudentCompareBy = compareByKey === compareByKeys.STUDENT
  const tableData = Object.keys(groupedByCompareByKey).map((compareByValue) => {
    const rowData = groupedByCompareByKey[compareByValue]
    const compareByLabelValue = rowData[0][compareByLabelKey]
    const tests = getTestColumnsData(rowData, userTestTypesInternal)
    return {
      id: compareByValue,
      [compareByKey]: compareByValue,
      [compareByLabelKey]: compareByLabelValue || '',
      districtId: rowData[0].districtId,
      districtName: rowData[0].districtName,
      ...(isStudentCompareBy
        ? {
            sisId: rowData[0].sisId || '',
            studentNumber: rowData[0].studentNumber || '',
            stateId: rowData[0].stateId || '',
          }
        : {}),
      tests,
    }
  })

  return tableData
}

// BACKEND SPECIFIC FUNCTIONS FOR CSV DOWNLOAD GENERATION START //

const getBackendCsvHeaderRows = (columnHeaders = {}) => {
  const {
    names = [],
    dates = [],
    testType = [],
    totalStudents = [],
    avgScore = [],
  } = columnHeaders
  return [names, dates, testType, totalStudents, avgScore].filter(
    (row) => row.length
  )
}

const normalizeCsvSettings = (settings = {}) => {
  // Client path passes `selectedCompareBy`; analyze-by may live on settings or separately.
  if (settings?.selectedCompareBy?.key) {
    return {
      selectedCompareBy: settings.selectedCompareBy,
      selectedAnalyzeBy: {
        key:
          settings?.selectedAnalyzeBy?.key ||
          settings?.analyzeBy?.key ||
          settings?.analyzeBy ||
          ANALYZE_BY_KEYS.AVERAGE_SCORE,
      },
      isDistrictGroupAdmin: !!settings?.isDistrictGroupAdmin,
    }
  }

  // Backend / async CSV path passes flat `compareBy` + `analyzeBy`.
  const compareByKey = settings?.compareBy?.key || settings?.compareBy
  const analyzeByKey = settings?.analyzeBy?.key || settings?.analyzeBy
  let compareByTitle =
    settings?.compareBy?.title || startCase(compareByKey || '')
  compareByTitle = compareByTitle.replace(/\s+Name$/i, '') || compareByTitle
  return {
    selectedCompareBy: {
      key: compareByKey,
      title: compareByTitle,
    },
    selectedAnalyzeBy: {
      key: analyzeByKey || ANALYZE_BY_KEYS.AVERAGE_SCORE,
    },
    isDistrictGroupAdmin: !!settings?.isDistrictGroupAdmin,
  }
}

const emptyToDash = (val) =>
  val == null || val === '' || String(val).includes('undefined') ? '-' : val

const STUDENT_CSV_ID_COLUMNS = [
  { title: 'SIS ID', dataIndex: 'sisId' },
  { title: 'STUDENT NUMBER', dataIndex: 'studentNumber' },
  { title: 'STATE ID', dataIndex: 'stateId' },
]

const padCsvMetaHeaderColumns = (columnHeaders, count = 1) => {
  for (let i = 0; i < count; i += 1) {
    columnHeaders.dates.push('-')
    columnHeaders.testType.push('-')
    columnHeaders.totalStudents.push('-')
    columnHeaders.avgScore.push('-')
  }
}

const formatDistributionForCsv = (
  test = {},
  selectedPerformanceBand = [],
  isStudentCompareBy = false
) => {
  const distribution = test.distribution || []
  if (!distribution.length) return '-'
  const testBand = test.externalTestType ? test.bands : selectedPerformanceBand
  const bandKey = test.externalTestType
    ? performanceBandKeys.EXTERNAL
    : performanceBandKeys.INTERNAL
  return distribution
    .map((d) => {
      const band = (testBand || []).find((b) => b[bandKey] === d.bandScore)
      let bandStr = `${band?.name || d.bandScore}`
      if (!isStudentCompareBy) {
        const totalStudents = test.totalStudentCount || test.totalGraded || 0
        const studentPercentage = totalStudents
          ? round((100 * d.totalStudents) / totalStudents)
          : 0
        bandStr += ` - ${studentPercentage}%`
      }
      return bandStr
    })
    .join(' | ')
}

const formatAverageScoreForCsv = (test = {}, externalScoreType) => {
  const score =
    test[externalScoreType] || test[EXTERNAL_SCORE_TYPES.SCALED_SCORE]
  return score || '-'
}

const getCsvRowsFromTableData = (
  tableData = [],
  filters = {},
  overallAssessmentsDataInput = [],
  selectedPerformanceBand = []
) => {
  const compareByKey = filters?.compareBy?.key || filters?.compareBy
  const analyzeByKey = filters?.analyzeBy?.key || filters?.analyzeBy
  const isAnalyzeByPerformanceDistribution =
    analyzeByKey === ANALYZE_BY_KEYS.PERFORMANCE_DISTRIBUTION
  const compareByLabelKey = compareByMap[compareByKey] || compareByKey
  const isDistrictGroupAdmin = !!filters?.isDistrictGroupAdmin
  const isStudentCompareBy = compareByKey === compareByKeys.STUDENT
  const { externalScoreType = EXTERNAL_SCORE_TYPES.SCALED_SCORE } = filters

  const allTestsMap = {}
  tableData.forEach((row) => {
    Object.assign(allTestsMap, row.tests || {})
  })
  const overallAssessmentsData = overallAssessmentsDataInput?.length
    ? overallAssessmentsDataInput
    : Object.values(allTestsMap).sort(
        (a, b) => (b.assessmentDate || 0) - (a.assessmentDate || 0)
      )

  const rows = tableData.map((row) => {
    const compareByLabelValue =
      row[compareByLabelKey] || row[compareByKey] || ''
    const csvRow = [emptyToDash(compareByLabelValue)]

    if (isStudentCompareBy) {
      STUDENT_CSV_ID_COLUMNS.forEach(({ dataIndex }) => {
        csvRow.push(emptyToDash(row[dataIndex]))
      })
    }

    if (
      isDistrictGroupAdmin &&
      [
        compareByKeys.SCHOOL,
        compareByKeys.TEACHER,
        compareByKeys.CLASS,
      ].includes(compareByKey)
    ) {
      csvRow.push(emptyToDash(row.districtName))
    }

    const rowTests = row.tests || {}
    overallAssessmentsData.forEach((assessment) => {
      const test = rowTests[assessment.testUniqId] || {}
      csvRow.push(
        isAnalyzeByPerformanceDistribution
          ? formatDistributionForCsv(
              test,
              selectedPerformanceBand,
              isStudentCompareBy
            )
          : formatAverageScoreForCsv(test, externalScoreType)
      )
    })

    if (!isAnalyzeByPerformanceDistribution) {
      overallAssessmentsData.forEach((assessment) => {
        const test = rowTests[assessment.testUniqId] || {}
        csvRow.push(test.band?.name || '-')
      })
    }

    return csvRow
  })

  return {
    csvRawData: rows,
    overallAssessmentsData,
  }
}

function getTestName(data) {
  const { isIncomplete, testName, externalTestType, shortTestName } = data
  const _testName = externalTestType ? shortTestName : testName
  return isIncomplete ? `${_testName} *` : _testName
}

const addDownloadCsvAdditionalColumns = (
  assessment,
  columnHeaders,
  userTestTypesInternal = {},
  externalScoreType
) => {
  const {
    assessmentDate,
    totalGraded,
    totalStudentCount,
    testType,
    externalTestTypeLabel,
    topLabels,
  } = assessment
  const scoreLabel =
    topLabels[externalScoreType] || topLabels[EXTERNAL_SCORE_TYPES.SCALED_SCORE]
  columnHeaders.dates.push(formatDate(assessmentDate))
  columnHeaders.testType.push(
    externalTestTypeLabel ||
      getAllTestTypeLabels(userTestTypesInternal)[testType] ||
      testType
  )
  const studentsCount =
    typeof totalGraded === 'number' ? totalGraded : totalStudentCount
  columnHeaders.totalStudents.push(
    typeof studentsCount === 'number' ? `${studentsCount}` : '-'
  )
  columnHeaders.avgScore.push(scoreLabel)
}

const getDownloadCsvColumnHeadersFunc = (
  settings,
  overallAssessmentsData = [],
  userTestTypesInternal = {},
  isDistrictGroupAdmin,
  externalScoreType = EXTERNAL_SCORE_TYPES.SCALED_SCORE
) => () => {
  const normalizedSettings = normalizeCsvSettings(settings)
  const {
    key: compareByKey,
    title: compareByTitle,
  } = normalizedSettings.selectedCompareBy

  const isAnalyzeByPerformanceDistribution =
    normalizedSettings.selectedAnalyzeBy.key ===
    ANALYZE_BY_KEYS.PERFORMANCE_DISTRIBUTION
  const resolvedIsDistrictGroupAdmin =
    typeof isDistrictGroupAdmin === 'boolean'
      ? isDistrictGroupAdmin
      : normalizedSettings.isDistrictGroupAdmin

  const dowloadCsvTableColumnHeaders = {
    names: [compareByTitle],
    dates: ['Date'],
    testType: ['Test Type'],
    totalStudents: ['Students'],
    avgScore: ['Avg. Score'],
  }

  if (compareByKey === compareByKeys.STUDENT) {
    STUDENT_CSV_ID_COLUMNS.forEach(({ title }) => {
      dowloadCsvTableColumnHeaders.names.push(title)
    })
    padCsvMetaHeaderColumns(
      dowloadCsvTableColumnHeaders,
      STUDENT_CSV_ID_COLUMNS.length
    )
  }

  if (
    resolvedIsDistrictGroupAdmin &&
    [compareByKeys.SCHOOL, compareByKeys.TEACHER, compareByKeys.CLASS].includes(
      compareByKey
    )
  ) {
    const districtColumnTitle = startCase(compareByKeys.DISTRICT)
    dowloadCsvTableColumnHeaders.names.push(districtColumnTitle)
    padCsvMetaHeaderColumns(dowloadCsvTableColumnHeaders)
  }

  overallAssessmentsData.forEach((assessment) => {
    const { externalTestType } = assessment
    const _testName = getTestName(assessment)
    const csvTableColumnHeader = isAnalyzeByPerformanceDistribution
      ? `${_testName} - Performance Distribution`
      : `${_testName}${externalTestType ? ' - Score' : ' - Score(%)'}`
    dowloadCsvTableColumnHeaders.names.push(csvTableColumnHeader)
    addDownloadCsvAdditionalColumns(
      assessment,
      dowloadCsvTableColumnHeaders,
      userTestTypesInternal,
      externalScoreType
    )
  })
  if (!isAnalyzeByPerformanceDistribution) {
    overallAssessmentsData.forEach((assessment) => {
      const _testName = getTestName(assessment)
      dowloadCsvTableColumnHeaders.names.push(`${_testName} - Performance Band`)
      addDownloadCsvAdditionalColumns(
        assessment,
        dowloadCsvTableColumnHeaders,
        userTestTypesInternal,
        externalScoreType
      )
    })
  }
  return dowloadCsvTableColumnHeaders
}
// BACKEND SPECIFIC FUNCTIONS FOR CSV DOWNLOAD GENERATION END //

module.exports = {
  ANALYZE_BY_AVERAGE_SCORE_TABLE_PAGE_SIZE,
  ANALYZE_BY_PERFORMANCE_DISTRIBUTION_TABLE_PAGE_SIZE,
  sortKeys,
  ANALYZE_BY_KEYS,
  ANALYZE_BY_OPTIONS,
  tableColumnsData,
  compareByMap,
  compareByOptionsInfo,
  getScoreLabel,
  getAchievementLevels,
  getProficiencyBand,
  getAllTestTypeLabels,
  getExternalScoreFormattedByType,
  getSelectedExternalScore,
  groupByCompareByKey,
  findTestWithAverageBand,
  getWeightedAchievementLevel,
  augmentBandData,
  getExternalScores,
  getChartData,
  getTableData,
  getTestName,
  getBackendCsvHeaderRows,
  getCsvRowsFromTableData,
  getDownloadCsvColumnHeadersFunc,
}
